import * as React from 'react';

import classNames from 'classnames';
import type moment from 'moment';

import { SvgButton, color } from '@coursera/coursera-ui';
import { SvgChevronLeft, SvgChevronRight, SvgEmptyCalendar } from '@coursera/coursera-ui/svg';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import DisplayDate from 'bundles/degree-home/components/DisplayDate';
import WeekRange from 'bundles/degree-home/models/WeekRange';
import { isPhoneOrSmaller } from 'bundles/phoenix/utils/matchMedia';

import _t from 'i18n!nls/degree-home';

import 'css!./__styl__/WeekSwitcher';

const TrackedSvgButton = withSingleTracked({ type: 'BUTTON' })(SvgButton);

type Props = {
  weekStartDate: moment.Moment;
  weekEndDate: moment.Moment;
  handleCurrentWeek: () => void;
  handlePreviousWeek: () => void;
  handleNextWeek: () => void;
};

class WeekSwitcher extends React.Component<Props> {
  render() {
    const { weekStartDate, weekEndDate, handleCurrentWeek, handlePreviousWeek, handleNextWeek } = this.props;

    const weekRange = new WeekRange({ weekStartDate, weekEndDate });
    const thisWeekButtonStyles = {
      opacity: 0.3,
      cursor: 'not-allowed',
      color: color.primaryText,
      backgroundColor: color.white,
      border: `1px solid ${color.divider}`,
    };
    const mobileWeekButtonStyles = {
      borderRadius: '2px',
      padding: '4px 12px',
    };
    const weekSwitcherClass = isPhoneOrSmaller()
      ? 'vertical-box align-items-horizonatal-center'
      : 'horizontal-box align-items-spacebetween align-items-vertical-center';

    return (
      <div className={classNames('rc-WeekSwitcher', weekSwitcherClass)}>
        <DisplayDate weekRange={weekRange} />

        <div className={classNames('horizontal-box', isPhoneOrSmaller() && 'buttons-mobile')}>
          <TrackedSvgButton
            type="default"
            size="zero"
            style={{
              borderRadius: '2px 0px 0px 2px',
              borderRight: 'transparent',
              padding: '0px 12px',
            }}
            onClick={handlePreviousWeek}
            svgElement={<SvgChevronLeft size={18} hoverColor={color.white} suppressTitle={true} />}
            trackingName="switch_previous_week"
            htmlAttributes={{
              'aria-label': _t('Switch to previous week'),
            }}
          />
          <TrackedSvgButton
            type="default"
            size="zero"
            style={{ marginRight: '12px', borderRadius: '0px 2px 2px 0px', padding: '0px 12px' }}
            onClick={handleNextWeek}
            svgElement={<SvgChevronRight size={18} hoverColor={color.white} suppressTitle={true} />}
            trackingName="switch_next_week"
            htmlAttributes={{
              'aria-label': _t('Switch to next week'),
            }}
          />

          <div className="bt3-visible-xs">
            <TrackedSvgButton
              type="default"
              size="zero"
              style={
                weekRange.isThisWeekDisabled()
                  ? {
                      ...thisWeekButtonStyles,
                      ...mobileWeekButtonStyles,
                    }
                  : {
                      ...mobileWeekButtonStyles,
                    }
              }
              onClick={handleCurrentWeek}
              svgElement={
                weekRange.isThisWeekDisabled() ? (
                  <SvgEmptyCalendar size={18} suppressTitle={true} />
                ) : (
                  <SvgEmptyCalendar size={18} hoverColor={color.white} suppressTitle={true} />
                )
              }
              trackingName="mobile_switch_this_week"
              htmlAttributes={{
                'aria-label': _t('Switch to this Week'),
                'aria-disabled': weekRange.isThisWeekDisabled(),
              }}
            />
          </div>

          <div className="bt3-hidden-xs">
            <TrackedSvgButton
              label={_t('This Week')}
              type="default"
              size="sm"
              style={weekRange.isThisWeekDisabled() ? thisWeekButtonStyles : {}}
              onClick={handleCurrentWeek}
              svgElement={
                weekRange.isThisWeekDisabled() ? (
                  <SvgEmptyCalendar size={18} suppressTitle={true} />
                ) : (
                  <SvgEmptyCalendar size={18} hoverColor={color.white} suppressTitle={true} />
                )
              }
              trackingName="switch_this_week"
              htmlAttributes={{
                'aria-label': _t('Switch to this Week'),
                'aria-disabled': weekRange.isThisWeekDisabled(),
              }}
            />
          </div>
        </div>
      </div>
    );
  }
}

export default WeekSwitcher;
